import { Constants } from '../../internal/Constants';
import Styles from './Image.module.css';
import { AssetsClient } from '@shared/frontend';
import { KeyboardEvent, Ref, useCallback, useEffect, useMemo, useState } from 'react';
import type { IAssetDTO } from '../../models/IAssetDTO';

/** Props for the Image component. */
interface IImageDTO {
  title: string;
  spriteUrl?: string;
  assetDTO?: IAssetDTO;
  disabled?: boolean;
  rtl?: boolean;
  classNameContainer?: string;
  classNameImageEnabled?: string;
  classNameImageDisabled?: string;
  classNameContainerLoading?: string;
  textLoading?: string;
  ref?: Ref<HTMLDivElement | null>;
  onClickHandler?: () => void;
}

/**
 * Renders an image control that loads an image asset from the specified manifest and bundle.
 *
 * @param props - Component props.
 * @returns The rendered image element.
 */
export function Image(props: IImageDTO) {
  const { title, ref, spriteUrl, assetDTO, disabled, rtl, classNameContainer, classNameImageEnabled, classNameImageDisabled, classNameContainerLoading, textLoading, onClickHandler } = props;
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Load image URL
  useEffect(() => {
    // Image loading
    const controller = new AbortController();
    (async () => {
      if (spriteUrl) {
        setImageUrl(spriteUrl);
        return;
      }
      if (assetDTO) {
        const uri = await AssetsClient.GetAssetURI(assetDTO.manifest, assetDTO.bundle, assetDTO.asset);
        if (!controller.signal.aborted) {
          setImageUrl(uri ?? null);
        }
      }
    })();
    // Cleanup
    return () => controller.abort();
  }, [assetDTO, spriteUrl]);

  // Handle key down to mimic click
  const handleKeyDown = useCallback((handler: () => void, e: KeyboardEvent) => {
    if (e.key === Constants.KeyMimicClick) {
      e.preventDefault();
      handler();
    }
  }, []);

  // Compute class names
  const containerClassNameLoading = useMemo(() => (classNameContainerLoading ? `${Styles.container} ${classNameContainerLoading}` : Styles.container), [classNameContainerLoading]);
  const containerClassName = useMemo(() => (classNameContainer ? `${Styles.container} ${classNameContainer}` : Styles.container), [classNameContainer]);
  const imageMainClassName = useMemo(() => (disabled ? Styles.disabled : Styles.enabled), [disabled]);
  // Clickable images get the pointer cursor; the class is gated so non-interactive or disabled images keep the default cursor.
  const isClickable = useMemo(() => Boolean(onClickHandler) && !disabled, [onClickHandler, disabled]);
  const imageClassName = useMemo(() => {
    const base = classNameImageEnabled ? `${imageMainClassName} ${classNameImageEnabled}` : classNameImageDisabled ? `${imageMainClassName} ${classNameImageDisabled}` : imageMainClassName;
    return isClickable ? `${base} ${Styles.clickable}` : base;
  }, [classNameImageEnabled, classNameImageDisabled, imageMainClassName, isClickable]);

  // Interactive props
  const interactiveProps = useMemo(() => {
    if (!onClickHandler) return {};
    const onClick = disabled ? undefined : onClickHandler;
    const onKeyDown = disabled ? undefined : handleKeyDown.bind(null, onClickHandler);
    return {
      tabIndex: 0,
      onClick,
      onKeyDown
    };
  }, [disabled, onClickHandler, handleKeyDown]);

  // Resolve title with RTL marker
  const resolvedTitle = useMemo(() => (rtl && title ? `\u200F${title}` : title), [rtl, title]);

  // Image loading
  if (!imageUrl) {
    return (
      <div ref={ref} className={containerClassNameLoading} title={resolvedTitle}>
        {textLoading && <>{textLoading}</>}
      </div>
    );
  }

  // Image loaded
  return (
    <div ref={ref} className={containerClassName}>
      <img src={imageUrl} alt={resolvedTitle} title={resolvedTitle} crossOrigin="anonymous" className={imageClassName} {...interactiveProps} />
    </div>
  );
}
